import { getSessionStorageValue, setSessionStorageValue } from '~/hooks/use-session-storage';

const SESSION_STORAGE_KEY = 'ENTITLEMENTS-SYNC-NEEDED';

// Long enough that a GET fired before the subscribe (onboarding mounts several useEntitlements()
// readers before the subscribe step) has certainly resolved. sync()'s freshness guard only blocks
// a NEW revalidation, not an already in-flight one landing after - clearing the flag after a single
// corrective sync just moves the race, so we wait this long and sync once more before clearing.
const SETTLE_WINDOW_MS = 10_000;

// Module-scoped: must survive the mount that discovered the flag unmounting before this fires.
let settleTimer: ReturnType<typeof setTimeout> | null = null;

/** Marks that a subscribe just completed and sync()'s cache write may lose a race to an earlier
 * reader's stale GET. Settled by settleEntitlementsSync (see use-entitlements.tsx). */
export function markEntitlementsSyncNeeded(): void {
  setSessionStorageValue(SESSION_STORAGE_KEY, Date.now());
}

function readMarkedAt(): number | null {
  const value = getSessionStorageValue<number>(SESSION_STORAGE_KEY);
  return typeof value === 'number' ? value : null;
}

function clearPendingEntitlementsSync(): void {
  setSessionStorageValue(SESSION_STORAGE_KEY, null);
}

/**
 * No-op unless a sync is pending. Otherwise fires an immediate corrective sync(), then schedules
 * exactly one final sync() once SETTLE_WINDOW_MS has passed since the subscribe - regardless of
 * further mounts - clearing the flag only once that final sync has fired. Elapsed time is read from
 * the stored mark, so a reload mid-window schedules only the remaining time.
 */
export function settleEntitlementsSync(sync: () => Promise<void>): void {
  const markedAt = readMarkedAt();
  if (markedAt === null || settleTimer) {
    return;
  }

  void sync();

  const remaining = SETTLE_WINDOW_MS - (Date.now() - markedAt);
  if (remaining <= 0) {
    clearPendingEntitlementsSync();
    return;
  }

  settleTimer = setTimeout(() => {
    settleTimer = null;
    void sync();
    clearPendingEntitlementsSync();
  }, remaining);
}
